import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, UserPlus, Plus, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { setSettingsCache } from "@/lib/settings";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/Bits";
import Modal from "@/components/Modal";

const TABS = [
  { id: "business", label: "Business" },
  { id: "invoicing", label: "Invoicing & Tax" },
  { id: "lists", label: "Lists & Stock" },
  { id: "users", label: "Users & Security" },
];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const toList = (v) => v.split(",").map((x) => x.trim()).filter(Boolean);

function Field({ label, hint, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="label">{label}</label>
      {children}
      {hint && <div className="text-xs text-[#8C929B] mt-1">{hint}</div>}
    </div>
  );
}

// Comma-separated list editor that keeps the raw text while typing.
function ListInput({ value, onChange, rows = 1, mono, testid }) {
  const [text, setText] = useState((value || []).join(", "));
  // Only re-sync from outside when the list really differs, so typing "a, " isn't reformatted mid-word.
  useEffect(() => {
    setText((t) => (toList(t).join("|") === (value || []).join("|") ? t : (value || []).join(", ")));
  }, [value]);
  const Tag = rows > 1 ? "textarea" : "input";
  return <Tag className={`field ${mono ? "font-num" : ""}`} rows={rows} value={text} data-testid={testid}
    onChange={(e) => { setText(e.target.value); onChange(toList(e.target.value)); }} />;
}

function UsersPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);
  const [reset, setReset] = useState(null);
  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success(`${form.name} can now sign in`); setForm(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const update = async (u, patch, msg) => {
    try { await api.put(`/users/${u.id}`, patch); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Section title="Team & logins" action={<button className="btn btn-primary btn-sm" onClick={() => setForm({ name: "", email: "", password: "", role: "staff" })} data-testid="add-user-btn"><UserPlus size={14} /> Add user</button>}>
      <table className="tbl">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Added</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {users.map((u) => {
            const me = u.id === user?.id;
            return (
              <tr key={u.id} className={u.active ? "" : "opacity-50"}>
                <td className="font-medium">{u.name}{me && <span className="text-xs text-[#8C929B]"> (you)</span>}</td>
                <td className="text-[#5C626A]">{u.email}</td>
                <td>
                  <select className="field py-1 w-auto" value={u.role} disabled={me} onChange={(e) => update(u, { role: e.target.value }, "Role updated")}>
                    <option value="owner">Owner</option><option value="staff">Staff</option>
                  </select>
                </td>
                <td className="text-[#5C626A]">{fmtDate(u.created_at)}</td>
                <td>{u.active ? <span className="chip border bg-[#EBF2EC] text-[#2D5A3C] border-[#B8D7BF]">Active</span> : <span className="chip border bg-[#F0EFF4] text-[#8C929B] border-[#D4CEBF]">Disabled</span>}</td>
                <td className="num whitespace-nowrap">
                  {!me && <button className="btn btn-ghost btn-sm mr-1" onClick={() => setReset({ user: u, password: "" })}>Reset password</button>}
                  {!me && <button className={`btn btn-sm ${u.active ? "btn-danger" : "btn-ghost"}`} onClick={() => update(u, { active: !u.active }, u.active ? `${u.name} can no longer sign in` : `${u.name} re-enabled`)}>{u.active ? "Disable" : "Enable"}</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-5 py-3 text-xs text-[#8C929B] border-t border-[#E6E1D8]">Owners see costs, profit, accounts and settings. Staff can sell, receive stock, record expenses and print bills.</div>

      <Modal open={!!form} onClose={() => setForm(null)} title="Add user" testid="user-form">
        {form && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name" wide><input className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></Field>
            <Field label="Email (used to sign in)" wide><input type="email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></Field>
            <Field label="Temporary password" hint="At least 8 characters. Ask them to change it after signing in."><input type="text" className="field font-num" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></Field>
            <Field label="Role"><select className="field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="staff">Staff</option><option value="owner">Owner</option></select></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={create} disabled={!form.name || !form.email || form.password.length < 8} data-testid="user-save">Add user</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!reset} onClose={() => setReset(null)} title={`Reset password · ${reset?.user.name}`}>
        {reset && (
          <div>
            <Field label="New password" hint="At least 8 characters. Share it with them privately."><input type="text" className="field font-num" value={reset.password} onChange={(e) => setReset({ ...reset, password: e.target.value })} /></Field>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn btn-ghost" onClick={() => setReset(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={reset.password.length < 8} onClick={async () => { await update(reset.user, { password: reset.password }, "Password reset"); setReset(null); }}>Reset password</button>
            </div>
          </div>
        )}
      </Modal>
    </Section>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [tab, setTab] = useState("business");
  const [dirty, setDirty] = useState(false);
  const [newScale, setNewScale] = useState({ name: "", sizes: "" });

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-[#8C929B]">Loading settings…</div>;

  const set = (k, v) => { setS({ ...s, [k]: v }); setDirty(true); };
  const save = async () => {
    try {
      const r = await api.put("/settings", s);
      setS(r.data); setSettingsCache(r.data); setDirty(false);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const gstinBad = s.gstin && !GSTIN_RE.test(s.gstin);
  const year = new Date().getFullYear();

  return (
    <div>
      <PageHeader eyebrow="Configuration" title="Settings" subtitle="Your business details, invoice format, tax, lists used across the app, and who can sign in.">
        {tab !== "users" && <button className="btn btn-primary" onClick={save} disabled={!dirty} data-testid="save-settings"><Save size={16} /> {dirty ? "Save changes" : "Saved"}</button>}
      </PageHeader>

      <div className="flex gap-1 border-b border-[#E6E1D8] mb-5">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === t.id ? "border-[#0E1B2E] text-[#0E1B2E] font-semibold" : "border-transparent text-[#5C626A] hover:text-[#0E1B2E]"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "business" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Section title="Company profile">
            <div className="p-5 grid grid-cols-2 gap-4">
              <Field label="Shop / brand name" wide><input className="field" value={s.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="set-company_name" /></Field>
              <Field label="Legal name" wide hint="As registered for GST, if different"><input className="field" value={s.legal_name} onChange={(e) => set("legal_name", e.target.value)} data-testid="set-legal_name" /></Field>
              <Field label="Address" wide><textarea className="field" rows={2} value={s.address} onChange={(e) => set("address", e.target.value)} data-testid="set-address" /></Field>
              <Field label="Phone"><input className="field" value={s.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
              <Field label="Email"><input className="field" value={s.email} onChange={(e) => set("email", e.target.value)} /></Field>
            </div>
          </Section>
          <Section title="Registration & year">
            <div className="p-5 grid grid-cols-2 gap-4">
              <Field label="GSTIN" hint={gstinBad ? "This doesn't look like a valid 15-character GSTIN" : "Printed on every invoice"}>
                <input className={`field font-num uppercase ${gstinBad ? "border-[#A3411D]" : ""}`} value={s.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase().trim())} maxLength={15} data-testid="set-gstin" />
              </Field>
              <Field label="State" hint="Place of supply"><input className="field" value={s.state} onChange={(e) => set("state", e.target.value)} /></Field>
              <Field label="Financial year starts in" hint="Used for 'This FY' in Accounts and Reports">
                <select className="field" value={s.fy_start_month} onChange={(e) => set("fy_start_month", Number(e.target.value))} data-testid="set-fy">
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </Field>
              <Field label="Currency"><input className="field" value={s.currency} disabled /></Field>
            </div>
          </Section>
          <Section title="Payment details (printed on invoices)">
            <div className="p-5 grid grid-cols-2 gap-4">
              <Field label="UPI ID" wide><input className="field font-num" value={s.upi_id} onChange={(e) => set("upi_id", e.target.value)} data-testid="set-upi_id" /></Field>
              <Field label="Bank name"><input className="field" value={s.bank_name} onChange={(e) => set("bank_name", e.target.value)} /></Field>
              <Field label="IFSC"><input className="field font-num uppercase" value={s.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} /></Field>
              <Field label="Account number" wide><input className="field font-num" value={s.bank_account} onChange={(e) => set("bank_account", e.target.value)} /></Field>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={s.show_bank_on_invoice !== false} onChange={(e) => set("show_bank_on_invoice", e.target.checked)} /> Show UPI and bank details on invoices
              </label>
            </div>
          </Section>
        </div>
      )}

      {tab === "invoicing" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Section title="Invoice numbering & credit">
            <div className="p-5 grid grid-cols-2 gap-4">
              <Field label="Invoice prefix" hint={`Next invoices look like ${(s.invoice_prefix || "INV").toUpperCase()}-${year}-000001`}>
                <input className="field font-num uppercase" value={s.invoice_prefix} maxLength={8} onChange={(e) => set("invoice_prefix", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} data-testid="set-invoice_prefix" />
              </Field>
              <Field label="Credit period (days)" hint="Due date for unpaid invoices"><input type="number" min="0" className="field font-num" value={s.default_due_days} onChange={(e) => set("default_due_days", Number(e.target.value))} data-testid="set-due-days" /></Field>
              <Field label="Terms & conditions" wide><textarea className="field" rows={3} value={s.terms} onChange={(e) => set("terms", e.target.value)} /></Field>
              <Field label="Invoice footer note" wide hint="e.g. 'Subject to Chennai jurisdiction'"><input className="field" value={s.invoice_notes} onChange={(e) => set("invoice_notes", e.target.value)} /></Field>
            </div>
          </Section>
          <Section title="GST">
            <div className="p-5 space-y-4">
              <Field label="Tax breakdown on invoices">
                <div className="flex gap-2">
                  {[["split", "CGST + SGST (within state)"], ["single", "Single GST line"]].map(([v, lbl]) => (
                    <button key={v} onClick={() => set("tax_mode", v)} data-testid={`tax-mode-${v}`}
                      className={`btn btn-sm flex-1 justify-center ${s.tax_mode === v ? "btn-primary" : "btn-ghost"}`}>{lbl}</button>
                  ))}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="GST rates in use (%)"><ListInput value={(s.tax_rates || []).map(String)} onChange={(v) => set("tax_rates", v.map(Number).filter((x) => !isNaN(x)))} mono /></Field>
                <Field label="Default rate for new products">
                  <select className="field" value={s.default_tax_rate} onChange={(e) => set("default_tax_rate", Number(e.target.value))}>
                    {[...new Set([0, ...(s.tax_rates || [])])].sort((a, b) => a - b).map((r) => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </Field>
              </div>
              <p className="text-xs text-[#8C929B]">Apparel is 5% GST up to ₹1,000 per piece in most cases — confirm current rates with your accountant.</p>
            </div>
          </Section>
          <Section title="Payment methods">
            <div className="p-5">
              <Field label="Ways customers pay and you pay" hint="Shown when taking payments, paying vendors and recording expenses. Cash-flow in Accounts is split by these.">
                <ListInput value={s.payment_methods} onChange={(v) => set("payment_methods", v)} testid="set-payment-methods" />
              </Field>
            </div>
          </Section>
        </div>
      )}

      {tab === "lists" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Section title="Expenses">
            <div className="p-5">
              <Field label="Expense categories" hint="Each category becomes a line in your Profit & Loss statement."><ListInput value={s.expense_categories} onChange={(v) => set("expense_categories", v)} rows={4} testid="set-expense-categories" /></Field>
            </div>
          </Section>
          <Section title="Products">
            <div className="p-5 space-y-4">
              <Field label="Categories"><ListInput value={s.product_categories} onChange={(v) => set("product_categories", v)} /></Field>
              <Field label="Product types"><ListInput value={s.product_types} onChange={(v) => set("product_types", v)} rows={2} /></Field>
              <Field label="Colour library" hint="Quick-pick colours when adding products"><ListInput value={s.color_library} onChange={(v) => set("color_library", v)} rows={2} /></Field>
              <Field label="Low-stock alert (default pcs per variant)"><input type="number" min="0" className="field font-num w-32" value={s.default_reorder_point} onChange={(e) => set("default_reorder_point", Number(e.target.value))} /></Field>
            </div>
          </Section>
          <Section title="Size scales" className="lg:col-span-2">
            <div className="p-5 space-y-3">
              {Object.entries(s.size_scales || {}).map(([name, sizes]) => (
                <div key={name} className="flex items-center gap-3">
                  <div className="w-28 text-sm font-medium capitalize">{name}</div>
                  <div className="flex-1"><ListInput value={sizes} onChange={(v) => set("size_scales", { ...s.size_scales, [name]: v })} mono /></div>
                  <button className="text-[#A3411D] p-1" title="Remove scale" onClick={() => { const n = { ...s.size_scales }; delete n[name]; set("size_scales", n); }}><X size={16} /></button>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2 border-t border-[#E6E1D8]">
                <input className="field w-28" placeholder="Scale name" value={newScale.name} onChange={(e) => setNewScale({ ...newScale, name: e.target.value })} />
                <input className="field flex-1 font-num" placeholder="Sizes, e.g. 2Y, 4Y, 6Y" value={newScale.sizes} onChange={(e) => setNewScale({ ...newScale, sizes: e.target.value })} />
                <button className="btn btn-ghost btn-sm" disabled={!newScale.name.trim() || !toList(newScale.sizes).length}
                  onClick={() => { set("size_scales", { ...s.size_scales, [newScale.name.trim().toLowerCase()]: toList(newScale.sizes) }); setNewScale({ name: "", sizes: "" }); }}><Plus size={14} /> Add</button>
              </div>
            </div>
          </Section>
        </div>
      )}

      {tab === "users" && <UsersPanel />}
    </div>
  );
}
